/** Formatting: times, countdowns, distances, crowd words. All local time.
 *  Rules (docs/11 §5): no seconds anywhere except "updated"; never a raw enum. */

const hm = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });
const wd = new Intl.DateTimeFormat(undefined, { weekday: 'short' });

/** Intl throws RangeError on an invalid date — and a thrown formatter blanks
 *  the whole screen. A bad timestamp renders as a dash instead. */
function safeTime(iso: string | null | undefined): string {
  const t = iso ? new Date(iso).getTime() : NaN;
  return Number.isFinite(t) ? hm.format(t) : '—';
}

export function fmtTime(iso: string): string {
  return safeTime(iso);
}

/** "9:40 – 10:10 PM" */
export function fmtWindow(p10: string, p90: string): string {
  return `${safeTime(p10)} – ${safeTime(p90)}`;
}

/** "18 min" · "2 h 40 min" · "3 h" — minutes only, never seconds. */
export function fmtDuration(ms: number): string {
  const min = Math.max(0, Math.round(ms / 60_000));
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

export type CountdownState = 'later' | 'soon' | 'now' | 'passed';

/**
 * Leave-by, the actionable number (docs/08 §8). 'later' beyond an hour,
 * 'soon' at ≤ 5 min (amber), 'now' within 90 s, 'passed' after.
 */
export function countdown(targetIso: string, nowMs: number): {
  label: string; state: CountdownState; minutes: number;
} {
  const delta = new Date(targetIso).getTime() - nowMs;
  const minutes = Math.round(delta / 60_000);
  if (delta < -90_000) return { label: 'Go now', state: 'passed', minutes };
  if (delta <= 90_000) return { label: 'Leave now', state: 'now', minutes: 0 };
  if (minutes <= 5) return { label: `${minutes} min`, state: 'soon', minutes };
  return { label: fmtDuration(delta), state: minutes > 60 ? 'later' : 'soon', minutes };
}

/** "Tomorrow" / "Fri" when a time falls on another calendar day, else ''. */
export function dayPrefix(iso: string, nowMs: number): string {
  const day = (ms: number) => new Date(ms).toDateString();
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t) || day(t) === day(nowMs)) return '';
  if (day(t) === day(nowMs + 86_400_000)) return 'Tomorrow';
  return wd.format(t);
}

const wdLong = new Intl.DateTimeFormat(undefined, { weekday: 'long', month: 'short', day: 'numeric' });

/** Timeline group key and label for a time: "Tonight", "Tomorrow", then
 *  "Thursday, Nov 6". A shift crosses midnight, so "tonight" runs to 5 am. */
export function dayGroup(iso: string, nowMs: number): { key: string; label: string } {
  if (isTonight(iso, nowMs)) return { key: 'tonight', label: 'Tonight' };
  const t = new Date(iso).getTime();
  const key = new Date(t).toDateString();
  if (key === new Date(nowMs + 86_400_000).toDateString()) return { key, label: 'Tomorrow' };
  return { key, label: wdLong.format(t) };
}

/** Tonight means "before 5 am tomorrow" — a shift crosses midnight. */
export function isTonight(iso: string, nowMs: number): boolean {
  const cutoff = new Date(nowMs);
  cutoff.setDate(cutoff.getDate() + 1);
  cutoff.setHours(5, 0, 0, 0);
  return new Date(iso).getTime() < cutoff.getTime();
}

export function agoSeconds(ms: number, nowMs: number): string {
  const s = Math.max(0, Math.round((nowMs - ms) / 1000));
  if (s < 90) return `${s} s ago`;
  return `${Math.round(s / 60)} min ago`;
}

export function etaLabel(band: string): string {
  switch (band) {
    case '<10': return 'under 10 min drive';
    case '10-20': return '10–20 min drive';
    case '20-30': return '20–30 min drive';
    case '30-45': return '30–45 min drive';
    case '45+': return 'over 45 min drive';
    default: return 'drive time unknown';
  }
}

export function demandLabel(band: string): string {
  switch (band) {
    case 'very_high': return 'Very large crowd';
    case 'high': return 'Large crowd';
    case 'medium': return 'Moderate crowd';
    default: return 'Smaller crowd';
  }
}
export function demandShort(band: string): string {
  switch (band) {
    case 'very_high': return 'Very large';
    case 'high': return 'Large';
    case 'medium': return 'Moderate';
    default: return 'Smaller';
  }
}

/** "4 Surge drivers heading here" — never a total-market claim (docs/07 P0-7). */
export function headingLabel(h: number | 'lt5'): string {
  if (h === 'lt5') return 'Fewer than 5 Surge drivers heading here';
  return `${h} Surge drivers heading here`;
}
export function headingShort(h: number | 'lt5'): string {
  return h === 'lt5' ? 'Under 5 heading there' : `${h} heading there`;
}

// ---- distance (drivers in the US think in miles; the API speaks km) ----
export const KM_PER_MI = 1.609344;
export const kmToMi = (km: number): number => km / KM_PER_MI;
export const miToKm = (mi: number): number => mi * KM_PER_MI;

export function fmtDistance(km: number, units: 'mi' | 'km' = 'mi'): string {
  if (units === 'km') return km < 10 ? `${km.toFixed(1)} km` : `${Math.round(km)} km`;
  const mi = kmToMi(km);
  return mi < 10 ? `${mi.toFixed(1)} mi` : `${Math.round(mi)} mi`;
}

/** Human category/subcategory words: never raw enum keys in the UI. */
export function categoryLabel(c: string): string {
  switch (c) {
    case 'sports': return 'Sports';
    case 'concert': return 'Concert';
    case 'theatre': return 'Theatre';
    case 'festival': return 'Festival';
    case 'conference': return 'Conference';
    case 'community': return 'Community';
    default: return c.replace(/_/g, ' ').replace(/^\w/, (m) => m.toUpperCase());
  }
}
export function subcategoryLabel(s: string | null): string | null {
  switch (s) {
    case 'nhl': return 'NHL game';
    case 'nba': return 'NBA game';
    case 'nfl': return 'NFL game';
    case 'mlb': return 'MLB game';
    case 'mls': return 'MLS match';
    case 'ncaa': return 'college game';
    case 'other_sport': return 'sports event';
    case 'arena': return 'arena show';
    case 'amphitheatre': return 'amphitheatre show';
    case 'club': return 'club night';
    case 'play': return 'play';
    case 'musical': return 'musical';
    case 'festival_day': return 'festival day';
    case 'convention': return 'convention';
    case 'expo': return 'expo';
    case 'community': return 'community event';
    default: return null;
  }
}

/** Every number carries its basis (docs/10 principle 3). */
export function basisText(opp: {
  egress: { basis: 'observed' | 'category_prior'; p10_at: string; p90_at: string };
  occurrence: { subcategory: string | null; category: string; starts_at: string };
}): string {
  const spread = Math.round((new Date(opp.egress.p90_at).getTime() - new Date(opp.egress.p10_at).getTime()) / 120_000);
  const what = subcategoryLabel(opp.occurrence.subcategory) ?? categoryLabel(opp.occurrence.category).toLowerCase();
  const started = `started ${fmtTime(opp.occurrence.starts_at)}`;
  return opp.egress.basis === 'observed'
    ? `End time from the organiser · ${what}, ${started} · crowd clears over about ${spread * 2} min`
    : `Estimated from a typical ${what}, ${started} · usually within ±${spread} min`;
}

export const REASON_TEXT: Record<string, (heading: number | 'lt5') => string> = {
  large_crowd: () => 'Large crowd, tight exit',
  few_surge_drivers: (h) => h === 'lt5' ? 'Only a few Surge drivers heading there' : `Only ${h} Surge drivers heading there`,
  good_timing_fit: () => 'You can get there before they come out',
  close_by: () => 'Short drive from you',
};
